import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Menu, X, Shield } from "lucide-react";
import LanguageSwitcher from "@/components/LanguageSwitcher";

export default function Navbar() {
  const { t } = useTranslation();
  const { lang } = useParams();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const navLinks = [
    { href: "#ka-tas-darbojas", label: t("nav.howItWorks") },
    { href: "#pakalpojumi", label: t("nav.services") },
    { href: "#privatpersonam", label: t("nav.privateClients") },
    { href: "#priekšrocības", label: t("nav.benefits") },
    { href: "#par-mums", label: t("nav.aboutUs") },
    { href: "#kontakti", label: t("nav.contacts") },
  ];

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToSection = (href) => {
    setIsMobileMenuOpen(false);
    const element = document.querySelector(href);
    if (element) {
      const offset = 80;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;
      window.scrollTo({
        top: offsetPosition,
        behavior: "smooth"
      });
    }
  };

  return (
    <header 
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled 
          ? "bg-white/95 nav-sticky shadow-sm" 
          : "bg-transparent"
      }`}
    >
      <nav className="section-container" aria-label="Galvenā navigācija">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <a 
            href={`/${lang || 'lv'}`} 
            className="flex items-center gap-3"
            data-testid="nav-logo"
          >
            <div className="w-10 h-10 bg-primary rounded-xl flex items-center justify-center">
              <Shield className="w-6 h-6 text-primary-foreground" />
            </div>
            <div>
              <span 
                className="text-xl font-bold text-foreground block"
                style={{ fontFamily: 'Manrope, sans-serif' }}
              >
                {t("nav.brand")}
              </span>
              <span className="text-xs text-muted-foreground">{t("nav.brandSub")}</span>
            </div>
          </a>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-8">
            {navLinks.map((link) => (
              <button
                key={link.href}
                onClick={() => scrollToSection(link.href)}
                className="text-foreground/80 hover:text-primary transition-colors font-medium"
                data-testid={`nav-link-${link.href.slice(1)}`}
              >
                {link.label}
              </button>
            ))}
          </div>

          {/* Desktop CTA + Language Switcher */}
          <div className="hidden lg:flex items-center gap-3">
            <LanguageSwitcher />
            <Button 
              onClick={() => scrollToSection("#kontakti")}
              className="btn-primary"
              data-testid="nav-cta-btn"
            >
              {t("nav.cta")}
            </Button>
          </div>

          {/* Mobile Menu Button + Language Switcher */}
          <div className="flex lg:hidden items-center gap-2">
            <LanguageSwitcher />
            <button
              className="p-2 text-foreground"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              data-testid="mobile-menu-btn"
            >
              {isMobileMenuOpen ? (
                <X className="w-6 h-6" />
              ) : (
                <Menu className="w-6 h-6" />
              )}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div 
            className="lg:hidden bg-white border-t border-border py-4 animate-fade-in"
            data-testid="mobile-menu"
          >
            <div className="flex flex-col gap-2">
              {navLinks.map((link) => (
                <button
                  key={link.href}
                  onClick={() => scrollToSection(link.href)}
                  className="text-left px-4 py-3 text-foreground/80 hover:text-primary hover:bg-secondary rounded-lg transition-colors font-medium"
                >
                  {link.label}
                </button>
              ))}
              <div className="px-4 pt-4">
                <Button 
                  onClick={() => scrollToSection("#kontakti")}
                  className="btn-primary w-full"
                  data-testid="mobile-cta-btn"
                >
                  {t("nav.cta")}
                </Button>
              </div>
            </div>
          </div>
        )}
      </nav>
    </header>
  );
}
